import {
  type AnimationEvent,
  type HTMLAttributes,
  type MouseEventHandler,
  type ReactNode,
  useCallback,
  useRef,
  useState,
} from 'react';
import { FocusOn } from 'react-focus-on';
import classnames from '@common/utils/ui/classnames';
import '@common/styles/components/overlay.pcss';

export interface OverlayProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  show?: boolean;
  noAnimation?: boolean;
  // use focusOn only for children that do NOT already use <FocusOn>.
  // Drawer or PopUp can lose focus to Overlay if focusOn is set inappropriately!
  focusOn?: boolean;
  dark?: boolean;
  onClick?: MouseEventHandler<HTMLDivElement>;
}

function Overlay({
  children,
  show = true,
  noAnimation = false,
  focusOn,
  dark,
  ...rest
}: OverlayProps) {
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  const handleFadeInAnimationStart = useCallback((event: AnimationEvent) => {
    if (event.animationName === 'overlay-fade-in') {
      setIsVisible(true);
    }
  }, []);

  const handleFadeOutAnimationEnd = useCallback((event: AnimationEvent) => {
    if (event.target === overlayRef.current && event.animationName === 'overlay-fade-out') {
      setIsVisible(false);
    }
  }, []);

  const content = (
    <div // NOSONAR:S6819 - Valid ARIA role="dialog" for overlay
      ref={overlayRef}
      className={classnames({
        overlay: true,
        'overlay--open': show,
        'overlay--closed': !show,
        'overlay--no-animation': noAnimation,
        'overlay--dark': dark,
      })}
      onAnimationStart={handleFadeInAnimationStart}
      onAnimationEnd={handleFadeOutAnimationEnd}
      role={focusOn ? 'dialog' : undefined}
      tabIndex={focusOn ? -1 : undefined}
      {...rest}
    >
      {children}
    </div>
  );

  if (!(show || isVisible)) {
    return null;
  }

  return focusOn
    ? (
      <FocusOn>
        {content}
      </FocusOn>
    )
    : content;
}

export default Overlay;
